import { useContext } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PricingProvider from '../../providers/Pricing/PricingProvider';
import NotificationProvider from '../../providers/Notification/NotificationProvider';
import BackgroundProvider from '../../providers/Background/BackgroundProvider';
import RowsProvider from '../../providers/Rows/RowsProvider';
import { updatePricingSheetById } from '../../services/pricingSheets';
import { useHistory } from 'react-router-dom';
import AcceptanceMatrix from './Components/AcceptanceMatrix';
import {
  PricingSheetRow,
  PricingSheetState,
} from '../../../../shared/types';
import { EditingRights } from './Components/EditingRights';
import useUserContext from '../../providers/User/UserProvider';
import calculateMeansByGroup from '../../utils/calculateMeansByGroup';
import { CustomerSummaryBox } from './Components/CustomerSummaryBox';
import { PricingSheetSummaryBox } from './Components/PricingSheetSummaryBox';
import { RequireApproval } from './Components/RequireApproval';
import { PRICE_FACTOR } from '../../../../shared/constants';
import { approvalValidatorPricing } from '../../helpers/approvalValidator';

const SummaryStep: React.FC = () => {
  const { pricingSheetId, rentalHistoryProvider } =
    useContext(PricingProvider);
  const { setNotification } = useContext(NotificationProvider);
  const { backgroundInfo } = useContext(BackgroundProvider);

  const { preSelectedRows } = useContext(RowsProvider);
  const { userEmail } = useUserContext();

  let history = useHistory();

  const {
    meanFilledCoefficientOfAllDayRows,
    meanFilledCoefficientOfAllDayRowsWeight,
    meanFilledCoefficientOfAllMonthRows,
    meanFilledCoefficientOfAllMonthRowsWeight,
  } = calculateMeansByGroup({
    rows: preSelectedRows,
  });
  const countProfit = (
    key:
      | 'rentalProfitDay'
      | 'rentalProfitMonth'
      | 'estimatedYieldMonth'
      | 'estimatedYieldDay',
  ) => {
    return preSelectedRows.reduce(
      (acc: number, cur: PricingSheetRow) => {
        const profit = cur[key] ?? 0;
        return Number(acc) + Number(profit);
      },
      0,
    );
  };

  const {
    result: approvalResults,
    requiredApprovalLevel,
    requiresApproval,
  } = approvalValidatorPricing(backgroundInfo, preSelectedRows);

  const approvePricingSheet = async () => {
    try {
      await updatePricingSheetById(pricingSheetId, {
        state: PricingSheetState.Approved,
      });
      setNotification({
        type: 'SNACKBAR',
        duration: 2000,
        severity: 'success',
        message: 'Hinnasto hyväksytty!',
      });
      history.push('/hinnastot');
    } catch (error) {
      console.error(error);
      setNotification({
        severity: 'error',
        message: 'Hinnaston hyväksyminen epäonnistui!',
      });
    }
  };
  return (
    <Box
      sx={{
        mt: 3,
      }}
    >
      <Typography
        variant="h4"
        component="div"
        gutterBottom
        color="primary"
        sx={{ ml: 1, mt: 1, mb: 0 }}
      >
        Yhteenveto
      </Typography>
      <Typography
        sx={{ ml: 1, mb: 2 }}
        variant="body1"
        color={'text.secondary'}
      >
        {backgroundInfo?.name ? backgroundInfo?.name : null}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { lg: 'column', xl: 'row' },
          gap: 6,
        }}
      >
        {/* LEFT COLUMN */}
        <Box
          sx={{
            width: { lg: '100%', xl: '40%' },
          }}
        >
          <CustomerSummaryBox
            customerName={backgroundInfo.customerName}
            revenue={backgroundInfo.revenue}
            potential={backgroundInfo.potential}
            industry={backgroundInfo.industry}
            customerType={backgroundInfo.customerType}
            rentalHistoryCustomerId={rentalHistoryProvider.customerId}
            rentalHistoryCompanyId={rentalHistoryProvider.companyId}
            rentalHistoryStartDate={rentalHistoryProvider.startDate}
            rentalHistoryEndDate={rentalHistoryProvider.endDate}
            rentalHistorySourceType={rentalHistoryProvider.sourceType}
            rentalHistoryCustomerName={rentalHistoryProvider.customer}
            rentalHistoryCompanyName={
              rentalHistoryProvider.companyName
            }
            pricingSheetRows={preSelectedRows}
          />

          <Box
            sx={{
              backgroundColor: '#e9edf1',
              padding: 4,
              borderRadius: 8,
              mt: 5,
            }}
          >
            <EditingRights
              pricingSheetId={pricingSheetId}
              editingRights={backgroundInfo.editingRights}
              userAllowedToDelete={
                backgroundInfo.userEmail === userEmail
              }
            />
          </Box>
        </Box>
        {/* RIGHT COLUMN */}
        <Box
          sx={{
            width: { lg: '100%', xl: '60%' },
            backgroundColor: '#e9edf1',
            padding: 4,
            borderRadius: 8,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h5" color="primary">
              Hyväksyntä
            </Typography>
          </Box>

          <PricingSheetSummaryBox
            meanFilledCoefficientOfAllDayRows={
              meanFilledCoefficientOfAllDayRows
            }
            meanFilledCoefficientOfAllMonthRowsWeight={
              meanFilledCoefficientOfAllMonthRowsWeight
            }
            meanFilledCoefficientOfAllMonthRows={
              meanFilledCoefficientOfAllMonthRows
            }
            meanFilledCoefficientOfAllDayRowsWeight={
              meanFilledCoefficientOfAllDayRowsWeight
            }
            paymentTermDays={backgroundInfo.paymentTermDays}
            preSelectedRows={preSelectedRows}
            potential={backgroundInfo.potential}
            priceSheetStartDate={backgroundInfo.priceSheetStartDate}
            priceSheetEndDate={backgroundInfo.priceSheetEndDate}
            pricingBasis={backgroundInfo.pricingBasis}
            priceSheetType={backgroundInfo.priceSheetType}
            totalProfitDay={
              countProfit('rentalProfitDay') / PRICE_FACTOR
            }
            totalProfitMonth={
              countProfit('rentalProfitMonth') / PRICE_FACTOR
            }
            totalEstimatedYieldDay={
              countProfit('estimatedYieldDay') / PRICE_FACTOR
            }
            totalEstimatedYieldMonth={
              countProfit('estimatedYieldMonth') / PRICE_FACTOR
            }
          />

          <Typography
            sx={{ mt: 2, fontSize: 18 }}
            variant="subtitle1"
            color={'primary'}
          >
            Hyväksymismatriisi
          </Typography>
          <AcceptanceMatrix approvalResults={approvalResults} />
          {requiresApproval && (
            <>
              <Typography
                sx={{ mt: 2, fontSize: 18 }}
                variant="subtitle1"
                color={'primary'}
              >
                Lähetä hyväksyntäpyyntö
              </Typography>
              <RequireApproval
                pricingSheetId={pricingSheetId}
                requiredApprovalLevel={requiredApprovalLevel as any}
              />
            </>
          )}
          {!requiresApproval && (
            <>
              <Typography
                sx={{ mt: 2, fontSize: 18 }}
                variant="subtitle1"
                color={'primary'}
              >
                Hinnaston hyväksyminen
              </Typography>
              <Typography variant="subtitle1">
                Hinnasto on hyväksyntärajojen sisällä ja voit hyväksyä
                hinnaston itse painamalla "Hyväksy"
              </Typography>
              <Button
                sx={{ mt: 2 }}
                variant="contained"
                onClick={approvePricingSheet}
              >
                Hyväksy
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SummaryStep;
